import React, { useEffect, useState, useCallback, Suspense } from 'react';
import { getStoreGroups } from 'store/requests/definitions';
import 'antd/dist/antd.css';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import trTR from 'antd/es/locale/tr_TR';
import { ConfigProvider } from 'antd';
import { debouncedDataLoadWaitMs, ORDER_TYPES } from 'helpers/constants';
import { Row, Button, Table, Col, Form, Tag, Dropdown } from 'antd';
import { FilterOutlined, MoreOutlined, EyeOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import userHaveRolePermission from 'helpers/userHaveRolePermission';
import { PAGE_URLS } from 'helpers/endpoints';

const Edit = React.lazy(() => import('./Edit'));
const StoreGroupsTableFilters = React.lazy(
  () => import('./StoreGroupsTableFilters'),
);
let lastQuery = false;

const StoreGroups = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filters, setFilters] = useState({});
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [tableParams, setTableParams] = useState({
    pagination: {
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total) => `Toplam ${total} kayıt`,
      pageSizeOptions: [20, 50, 100],
      size: 'small',
      current: 1,
      pageSize: 20,
    },
  });

  useEffect(() => {
    fetchData();
    setIsFiltersOpen(false);
    return () => {
      lastQuery = null;
      setLoading(false);
    };
  }, [JSON.stringify(tableParams), JSON.stringify(filters)]);

  const fetchData = () => {
    const queryString = getQueryString();
    loadData(queryString);
  };

  const columns = [
    {
      title: t('branchName'),
      dataIndex: 'name',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('branchCode'),
      dataIndex: 'code',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('branchManager'),
      dataIndex: 'adminNameSurname',
      sorter: true,
      showSorterTooltip: false,
      render: (text, record) => <p>{record?.manager?.nameSurname || '--'}</p>,
    },
    {
      title: t('branchManagerMail'),
      dataIndex: 'adminEmail',
      sorter: true,
      render: (text, record) => <p>{record?.manager?.email || '--'}</p>,
    },
    {
      title: t('branchManagerPhoneNum'),
      dataIndex: 'adminPhone',
      sorter: true,
      render: (text, record) => <p>{record?.manager?.phoneNumber || '--'}</p>,
    },
    {
      width: 50,
      render: (dataItem) =>
        isCrudEnabled && (
          <Dropdown
            menu={{
              items: [
                {
                  label: t('showDetails'),
                  icon: <EyeOutlined />,
                  onClick: () => {
                    setIsEditModalOpen(true);
                  },
                },
              ],
            }}
            placement="bottomRight"
            arrow
            trigger={['click']}
            onClick={() => setSelectedItem(dataItem)}
          >
            <Button shape="circle" icon={<MoreOutlined />}></Button>
          </Dropdown>
        ),
    },
  ];

  const renderSelectedFilters = () => {
    const fieldLocalizationVars = {
      name: 'branchName',
      code: 'branchCode',
      nameSurname: 'branchManager',
      email: 'branchManagerMail',
      phoneNumber: 'branchManagerPhoneNum',
    };
    return Object.keys(filters).map((item, idx) => {
      if (filters[item]) {
        return (
          <Tag
            key={idx}
            closable
            color="geekblue"
            onClose={(e) => {
              e.preventDefault();
              removeItemFromFilters(item);
            }}
          >
            {`${t(fieldLocalizationVars[item])} : ${filters[item]}`}
          </Tag>
        );
      }
    });
  };

  const removeItemFromFilters = (destinationItem) => {
    Object.keys(filters).forEach((item) => {
      if (item === destinationItem) {
        const editedFilters = {
          ...filters,
          [destinationItem]: undefined,
        };
        setFilters(editedFilters);
        form.setFieldsValue(editedFilters);
      }
    });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      ...sorter,
    });
  };

  const getQueryString = () => {
    let queryString = '';

    queryString = Object.entries(filters)
      .filter(
        ([item, value]) =>
          value != null && value !== undefined && filters[item],
      )
      .map(([item, value]) => `${item}=${encodeURI(value)}`)
      .join('&');

    queryString += `&page=${tableParams.pagination.current - 1}&size=${tableParams.pagination.pageSize}`;

    const { column } = tableParams;
    if (column) {
      const columnMapping = {
        adminNameSurname: 'nameSurname',
        adminEmail: 'email',
        adminPhone: 'phoneNumber',
      };

      queryString += column
        ? `&sort=${columnMapping[column.dataIndex] || column.dataIndex},${ORDER_TYPES[tableParams.order]}`
        : '';
    }

    return queryString;
  };

  async function loadDataWithDebounce(queryString) {
    if (!loading) {
      lastQuery = queryString;
      setLoading(true);
      getStoreGroups(queryString)
        .then((storeGroup) => {
          const { content, page } = storeGroup;
          setRows(content ? content : []);
          setTableParams({
            ...tableParams,
            pagination: {
              ...tableParams.pagination,
              total: page.totalElements,
            },
          });
        })
        .finally(() => setLoading(false));
    }
  }

  const loadData = useCallback(
    debounce((queryString) => {
      loadDataWithDebounce(queryString);
    }, debouncedDataLoadWaitMs),
  );

  return (
    <div style={{ position: 'relative' }}>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
        }}
      >
        <Col></Col>
        <Col>
          <Button
            shape="round"
            icon={<FilterOutlined />}
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          >
            {t('filters')}
          </Button>
        </Col>
      </Row>
      <Row
        style={{ display: 'flex', justifyContent: 'flex-end', margin: '5px' }}
      >
        {renderSelectedFilters()}
      </Row>
      <Row style={{ marginTop: '10px' }}>
        <ConfigProvider locale={trTR}>
          <Table
            style={{ width: '100%' }}
            size="small"
            columns={columns}
            dataSource={rows}
            pagination={tableParams.pagination}
            loading={loading}
            onChange={handleTableChange}
            rowKey={(record, index) => record.code + index}
            scroll={{
              y: '70vh',
            }}
          />
        </ConfigProvider>
      </Row>
      <Suspense fallback={() => {}}>
        {isFiltersOpen && (
          <StoreGroupsTableFilters
            setIsFiltersOpen={setIsFiltersOpen}
            setFilters={setFilters}
            form={form}
          />
        )}
        {isEditModalOpen && (
          <Edit
            storeGroup={selectedItem}
            onCloseHandler={() => setIsEditModalOpen(false)}
          />
        )}
      </Suspense>
    </div>
  );
};

export default StoreGroups;
